"use client"

import type { CommonParams } from "@/features/advices/types"
import type { FocusEventHandler, FormEventHandler, FormHTMLAttributes, FunctionComponent } from "react"

import { startHolyLoader } from "holy-loader"
import { useParams, useRouter } from "next/navigation"
import { useRef } from "react"

import { Search } from "@jobteaser/spark/components/Icons/Search"
import { TextInput } from "@jobteaser/spark/components/TextInput"

import { useLocale } from "@/modules/locales/useLocale"

type FormProps = FormHTMLAttributes<HTMLFormElement> & {
  ariaLabel: string
  clearIconAriaLabel: string
  placeholder: string
}

export const Form: FunctionComponent<FormProps> = ({ ariaLabel, clearIconAriaLabel, placeholder, ...props }) => {
  const locale = useLocale()
  const { query } = useParams<CommonParams>()
  const { push } = useRouter()
  const formRef = useRef<HTMLFormElement>(null)

  const submitForm = (): void => {
    if (!formRef.current) {
      return
    }

    const formData = new FormData(formRef.current)
    const queryValue = ((formData.get("query") as string | null) || "").trim()

    if (queryValue) {
      startHolyLoader()
      push(`/${locale}/advice_search/${queryValue}`)
    }
  }

  const handleFormSubmission: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault()
    submitForm()
  }

  const handleFieldClearance = (): void => {
    startHolyLoader()
    push(`/${locale}/advices`)
  }

  const handleFieldUpdate: FocusEventHandler<HTMLInputElement> = event => {
    event.preventDefault()
    submitForm()
  }

  return (
    <form {...props} onSubmit={handleFormSubmission} ref={formRef}>
      <TextInput
        aria-label={ariaLabel}
        clearIconAriaLabel={clearIconAriaLabel}
        hasClearIcon
        id="query"
        leftIcon={Search}
        name="query"
        onBlur={handleFieldUpdate}
        onClear={handleFieldClearance}
        placeholder={placeholder}
        value={decodeURIComponent(query || "")}
      />
    </form>
  )
}
